/**
 * Tax Configuration API service.
 *
 * Promise-based wrapper around the real backend endpoints under
 * `api/v1/TaxConfig` (+ `api/v1/TaxCategory`). Mirrors the axios/base-URL/
 * cookie-auth pattern used by the rest of ClientApp (`src/api/call.js`,
 * `services/*`). All responses are wrapped in the standard ResponseModel
 * `{ message, status, data }`; helpers below unwrap `.data` and throw on a
 * non-success `status` (APIStatus.Successful === 0) so callers can `await`
 * the inner payload directly and `catch` a friendly Error.
 *
 * NOTE (JS project — no TypeScript): shapes documented via JSDoc typedefs.
 *
 * @typedef {Object} TaxYearDto
 * @property {number} yearID @property {string} taxYearCode @property {number} beYear
 * @property {number} year @property {string} description @property {boolean} isActive
 * @property {?string} effectiveStartDate @property {?string} effectiveEndDate
 *
 * @typedef {Object} TaxConfigVersionDto
 * @property {number} versionID @property {number} yearID @property {string} versionCode
 * @property {string} description @property {boolean} isActive
 * @property {?string} effectiveStartDate @property {?string} effectiveEndDate
 *
 * @typedef {Object} TaxConfigRuleDto
 * @property {number} ruleID @property {number} versionID @property {string} categoryCode
 * @property {string} ruleCode @property {string} label @property {?string} type
 * @property {number} sortOrder @property {boolean} isActive
 *
 * @typedef {Object} TaxConfigCategoryGroupDto
 * @property {string} categoryCode @property {string} categoryLabel @property {number} sortOrder
 * @property {TaxConfigRuleDto[]} rules
 *
 * @typedef {Object} TaxConfigFullDto
 * @property {TaxConfigVersionDto} version @property {TaxConfigCategoryGroupDto[]} categories
 *
 * ── Bundle export / import contract (whole tax-year config bundle) ──────
 *
 * @typedef {Object} BundleTaxYearDto
 * @property {string} taxYearCode @property {number} beYear @property {number} year
 * @property {string} description
 * @property {?string} effectiveStartDate @property {?string} effectiveEndDate
 *
 * @typedef {Object} BundleConfigVersionDto
 * @property {string} versionCode @property {string} description
 * @property {?string} effectiveStartDate @property {?string} effectiveEndDate
 *
 * @typedef {Object} BundleCategoryDto
 * @property {string} categoryCode @property {string} labelTh @property {string} labelEn
 * @property {string} labelCn @property {number} sortOrder
 *
 * @typedef {Object} BundleRuleDto
 * @property {string} categoryCode @property {string} ruleCode @property {string} label
 * @property {?string} type @property {number} sortOrder @property {boolean} isActive
 *
 * @typedef {Object} BundleFormulaDto
 * @property {string} outputVar @property {?string} expressionGraphJson
 *
 * @typedef {Object} BundleBracketDto
 * @property {number} lowerBound @property {?number} upperBound @property {number} rate
 *
 * @typedef {Object} BundleConstantDto
 * @property {string} constKey @property {number} value
 *
 * @typedef {Object} BundleCalcVersionDto
 * @property {string} versionCode @property {string} description
 * @property {?string} canvasGraphJson
 * @property {BundleFormulaDto[]} formulas @property {BundleBracketDto[]} brackets
 * @property {BundleConstantDto[]} constants
 *
 * @typedef {Object} BundleQuestionVersionDto
 * @property {string} versionCode @property {string} description
 * @property {Object[]} steps @property {Object[]} items
 *
 * @typedef {Object} TaxConfigBundleDto
 * @property {string} schemaVersion @property {string} kind @property {string} exportedAt
 * @property {BundleTaxYearDto} taxYear @property {BundleConfigVersionDto} configVersion
 * @property {BundleCategoryDto[]} categories @property {BundleRuleDto[]} rules
 * @property {BundleCalcVersionDto[]} calcVersions
 * @property {BundleQuestionVersionDto[]} questionVersions
 *
 * @typedef {Object} ImportTargetDto
 * @property {('newYear'|'existingYear')} mode @property {?number} yearId
 * @property {?number} beYear @property {?string} taxYearCode @property {?string} description
 * @property {?string} effectiveStartDate @property {?string} effectiveEndDate
 * @property {?string} configVersionDescription @property {boolean} configVersionIsActive
 *
 * @typedef {Object} ImportTaxConfigBundleRequest
 * @property {TaxConfigBundleDto} bundle @property {ImportTargetDto} target
 *
 * @typedef {Object} ImportTaxConfigResultDto
 * @property {number} yearId @property {number} versionId @property {string} versionCode
 * @property {boolean} versionCodeAdjusted @property {number} ruleCount
 * @property {number} calcVersionCount @property {number} questionVersionCount
 * @property {string[]} warnings
 */

import axios from 'axios';

const BASE_URL = import.meta.env.VITE_APP_API || 'http://localhost:5000/';

// Dedicated instance. `withCredentials` is set explicitly (cookie session auth)
// so it does not depend on the global axios default being applied first.
const http = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

const API_STATUS_SUCCESS = 0; // APIStatus.Successful

/**
 * Unwrap a ResponseModel `{ message, status, data }` → inner `data`.
 * Throws an Error (with `.status`/`.data`) when the envelope reports failure.
 */
function unwrap(response) {
  const body = response && response.data;
  if (body && typeof body === 'object' && 'status' in body) {
    if (body.status === API_STATUS_SUCCESS) {
      return body.data;
    }
    const err = new Error(body.message || 'Request failed');
    err.status = body.status;
    err.data = body.data;
    throw err;
  }
  return body;
}

/** Normalize an axios/transport error into a friendly Error carrying the API message. */
function toError(error) {
  const body = error && error.response && error.response.data;
  const message =
    (body && (body.message || body.Message)) ||
    (error && error.message) ||
    'Request failed';
  const err = new Error(message);
  err.httpStatus = error && error.response && error.response.status;
  err.status = body && (body.status ?? body.Status);
  err.data = body && (body.data ?? body.Data);
  return err;
}

async function get(url) {
  try {
    return unwrap(await http.get(url));
  } catch (e) {
    throw e instanceof Error && e.httpStatus === undefined && e.status !== undefined ? e : toError(e);
  }
}
async function post(url, payload) {
  try {
    return unwrap(await http.post(url, payload));
  } catch (e) {
    throw e instanceof Error && e.httpStatus === undefined && e.status !== undefined ? e : toError(e);
  }
}
async function put(url, payload) {
  try {
    return unwrap(await http.put(url, payload));
  } catch (e) {
    throw e instanceof Error && e.httpStatus === undefined && e.status !== undefined ? e : toError(e);
  }
}
async function patch(url, payload) {
  try {
    return unwrap(await http.patch(url, payload));
  } catch (e) {
    throw e instanceof Error && e.httpStatus === undefined && e.status !== undefined ? e : toError(e);
  }
}
async function del(url) {
  try {
    return unwrap(await http.delete(url));
  } catch (e) {
    throw e instanceof Error && e.httpStatus === undefined && e.status !== undefined ? e : toError(e);
  }
}

const ROOT = 'api/v1/TaxConfig';

export const taxConfigApi = {
  // ── Tax Years (Level 1) ──────────────────────────────────────────────
  /** @returns {Promise<TaxYearDto[]>} */
  getYears(includeInactive = true) {
    return get(`${ROOT}/years?includeInactive=${includeInactive}`);
  },
  /** @returns {Promise<TaxYearDto>} */
  createYear(body) {
    return post(`${ROOT}/years`, body);
  },
  /** @returns {Promise<TaxYearDto>} */
  updateYear(id, body) {
    return put(`${ROOT}/years/${id}`, body);
  },
  /** @returns {Promise<boolean>} */
  setYearActive(id, isActive) {
    return patch(`${ROOT}/years/${id}/active`, { isActive: Boolean(isActive) });
  },
  /** @returns {Promise<boolean>} */
  deleteYear(id) {
    return del(`${ROOT}/years/${id}`);
  },

  // ── Config Versions (Level 2 metadata) ───────────────────────────────
  /** @returns {Promise<TaxConfigVersionDto[]>} */
  getVersions(yearId = null, includeInactive = true) {
    let url = `${ROOT}/versions?includeInactive=${includeInactive}`;
    if (yearId != null) url += `&yearId=${yearId}`;
    return get(url);
  },
  /** @returns {Promise<TaxConfigVersionDto>} */
  createVersion(body) {
    return post(`${ROOT}/versions`, body);
  },
  /** @returns {Promise<TaxConfigVersionDto>} */
  updateVersion(id, body) {
    return put(`${ROOT}/versions/${id}`, body);
  },
  /** @returns {Promise<boolean>} (siblings auto-deactivated server-side) */
  setVersionActive(id, isActive) {
    return patch(`${ROOT}/versions/${id}/active`, { isActive: Boolean(isActive) });
  },
  /** @returns {Promise<boolean>} */
  deleteVersion(id) {
    return del(`${ROOT}/versions/${id}`);
  },

  // ── Rules (Level 2 content) ───────────────────────────────────────────
  /** @returns {Promise<TaxConfigFullDto>} */
  getFullConfig(versionId, includeInactive = true) {
    return get(`${ROOT}/versions/${versionId}/full?includeInactive=${includeInactive}`);
  },
  /** @returns {Promise<TaxConfigRuleDto[]>} */
  getRules(versionId, categoryCode = null, includeInactive = true) {
    let url = `${ROOT}/versions/${versionId}/rules?includeInactive=${includeInactive}`;
    if (categoryCode) url += `&categoryCode=${encodeURIComponent(categoryCode)}`;
    return get(url);
  },
  /** @returns {Promise<TaxConfigRuleDto>} */
  getRule(ruleId) {
    return get(`${ROOT}/rules/${ruleId}`);
  },
  /** @returns {Promise<TaxConfigRuleDto>} */
  createRule(versionId, body) {
    return post(`${ROOT}/versions/${versionId}/rules`, body);
  },
  /**
   * Append a SINGLE boolean "flag" rule to a version (inline flag creation from the
   * Tax Question editor). Ensures the CTRL flow-control category server-side and forces
   * type=boolean — never full-replaces the rule set.
   * @param {number} versionId
   * @param {{label:string, ruleCode?:string, type?:string, categoryCode?:string}} body
   * @returns {Promise<TaxConfigRuleDto>}
   */
  appendFlag(versionId, body) {
    return post(`${ROOT}/versions/${versionId}/rules/append`, body);
  },
  /** @returns {Promise<TaxConfigRuleDto>} */
  updateRule(ruleId, body) {
    return put(`${ROOT}/rules/${ruleId}`, body);
  },
  /** @returns {Promise<boolean>} */
  setRuleActive(ruleId, isActive) {
    return patch(`${ROOT}/rules/${ruleId}/active`, { isActive: Boolean(isActive) });
  },
  /** @returns {Promise<boolean>} */
  deleteRule(ruleId) {
    return del(`${ROOT}/rules/${ruleId}`);
  },
  /**
   * Replace the whole rule set of a version (form editor "Save") — transactional.
   * @param {number} versionId
   * @param {Object[]} rulesArray array of CreateTaxConfigRuleRequest-shaped objects
   * @returns {Promise<TaxConfigRuleDto[]>}
   */
  bulkSaveRules(versionId, rulesArray) {
    return put(`${ROOT}/versions/${versionId}/rules/bulk`, { rules: rulesArray || [] });
  },

  // ── Bundle export / import (whole-year config bundle) ────────────────
  /**
   * Export a config version + its rules/calc versions/question versions as a
   * portable, business-key-only bundle (server strips ids/audit).
   * @param {number} versionId
   * @returns {Promise<TaxConfigBundleDto>} the unwrapped bundle object
   */
  exportBundle(versionId) {
    return get(`${ROOT}/versions/${versionId}/export`);
  },
  /**
   * Import a bundle under a target year/version — persisted atomically server-side,
   * resolving/creating the year, auto-provisioning categories, and auto-incrementing
   * the config version code on conflict (never overwrites).
   * @param {ImportTaxConfigBundleRequest} payload `{ bundle, target }`
   * @returns {Promise<ImportTaxConfigResultDto>} the import result
   */
  importBundle(payload) {
    return post(`${ROOT}/import`, payload);
  },

  // ── Category master (dropdown source) ────────────────────────────────
  /** @returns {Promise<Object[]>} active TaxCategoryDto[] */
  getCategories() {
    return get('api/v1/TaxCategory');
  },
};

export default taxConfigApi;
